import type { Project } from '@servicetim/shared';
import { useRenameProjectKey } from '@servicetim/ui/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
} from '@servicetim/ui/components';
import { useId, useState } from 'react';
import { problemDetail } from '../lib/field-errors';
import { normalizeProjectKey, projectKeyProblem } from '../lib/project-key';
import { FormRow } from '../ui/form-row';

export interface RenameKeyDialogProps {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Rename the project key; the old key becomes an alias so existing ticket keys keep resolving. */
export function RenameKeyDialog({ project, open, onOpenChange }: RenameKeyDialogProps) {
  const id = useId();
  const rename = useRenameProjectKey();
  const [key, setKey] = useState(project.key);
  const problem = key === project.key ? 'Choose a different key' : projectKeyProblem(key);
  const taken = problemDetail(rename.error) === 'project.key_taken';

  const submit = async () => {
    if (problem) return;
    try {
      await rename.mutateAsync({ id: project.id, key });
      toast.success(`Project key is now ${key}`);
      onOpenChange(false);
    } catch {
      // banner
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setKey(project.key);
          rename.reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Rename project key</DialogTitle>
          <DialogDescription>
            New tickets get the new key (for example{' '}
            <code className="font-mono">{key || 'KEY'}-1</code>
            ). Existing ticket keys such as <code className="font-mono">{project.key}-42</code> keep
            working through an alias; the old key cannot be reused by another project.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {rename.error && !taken ? <ProblemBanner error={rename.error} size="sm" /> : null}
          <FormRow
            label="New key"
            htmlFor={`${id}-key`}
            required
            error={taken ? 'This key is already taken' : key.length > 0 ? problem : null}
          >
            <Input
              id={`${id}-key`}
              value={key}
              onChange={(e) => setKey(normalizeProjectKey(e.target.value))}
              maxLength={10}
              className="w-40 font-mono"
              autoComplete="off"
              aria-invalid={taken || (key.length > 0 && problem) ? true : undefined}
            />
          </FormRow>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={rename.isPending || problem !== null}>
              Rename key
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
